/**
 * Servicio de Configuración Centralizada
 * Maneja toda la configuración de la aplicación de forma unificada
 */

import {
  getAppConfig,
  getAuthConfig,
  isMultieventEnabled,
  getDefaultClient,
  getDefaultEventID
} from '../config/app.config';
import { Storage } from '@capacitor/storage';

export interface LoginRequestData {
  ced: string;
  eventID?: string;
  multievent: boolean;
  client: string;
  action?: string; // Parámetro opcional para acciones específicas
}

// Interface removida - SmartLogin no implementado

export class ConfigService {
  private static instance: ConfigService;
  
  private constructor() {}
  
  public static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }
  
  /**
   * Obtiene la configuración completa de la aplicación
   */
  public getConfig() {
    return getAppConfig();
  }
  
  /**
   * Obtiene la configuración de autenticación
   */
  public getAuthConfig() {
    return getAuthConfig();
  }
  
  /**
   * Verifica si multievent está habilitado
   */
  public isMultieventEnabled(): boolean {
    return isMultieventEnabled();
  }
  
  /**
   * Obtiene el cliente por defecto
   */
  public getDefaultClient(): string {
    return getDefaultClient();
  }

  /**
   * Obtiene el evento por defecto
   */
  public getDefaultEventID(): string | undefined {
    return getDefaultEventID();
  }
  
  // Smart login removido - no implementado
  
  /**
   * Construye los datos de request para login con múltiples eventos
   */
  public buildMultiEventLoginRequest(ced: string): LoginRequestData {
    const isMultievent = this.isMultieventEnabled();
    const request: LoginRequestData = {
      ced,
      multievent: isMultievent,
      client: this.getDefaultClient()
    };

    // Si multievent es false, agregar el evento por defecto
    if (!isMultievent) {
      const defaultEventID = this.getDefaultEventID();
      if (defaultEventID) {
        request.eventID = defaultEventID;
      }
    }

    return request;
  }
  
  /**
   * Construye los datos de request para login con evento específico
   */
  public buildSingleEventLoginRequest(ced: string, eventID: string): LoginRequestData {
    return {
      ced,
      eventID,
      multievent: false, // Siempre false para eventos específicos
      client: this.getDefaultClient()
    };
  }
  
  // Smart login removido - no implementado
  
  /**
   * Obtiene la URL del endpoint de login
   * En desarrollo usa proxy para evitar CORS
   */
  public getLoginEndpoint(): string {
    // En desarrollo, usar proxy para evitar CORS
    if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
      return '/api/login';
    }

    // En producción, usar URL directa
    return 'https://esimposio.com/miesimposio/app_login/login.php';
  }
  
  /**
   * Obtiene las opciones de request configuradas para login
   */
  public getLoginRequestOptions(data: LoginRequestData): RequestInit {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    };
  }
  
  /**
   * Método de conveniencia para hacer login con múltiples eventos
   */
  public async loginMultiEvent(ced: string): Promise<Response> {
    const requestData = this.buildMultiEventLoginRequest(ced);
    const requestOptions = this.getLoginRequestOptions(requestData);
    const endpoint = this.getLoginEndpoint();
    
    console.log('🔄 ConfigService: Login multi-evento', {
      endpoint,
      multievent: requestData.multievent,
      client: requestData.client
    });
    
    return fetch(endpoint, requestOptions);
  }
  
  /**
   * Método de conveniencia para hacer login con evento específico
   */
  public async loginSingleEvent(ced: string, eventID: string): Promise<Response> {
    const requestData = this.buildSingleEventLoginRequest(ced, eventID);
    const requestOptions = this.getLoginRequestOptions(requestData);
    const endpoint = this.getLoginEndpoint();
    
    console.log('🔄 ConfigService: Login evento específico', {
      endpoint,
      eventID,
      multievent: requestData.multievent,
      client: requestData.client
    });
    
    return fetch(endpoint, requestOptions);
  }
  
  // Smart login removido - no implementado
  
  /**
   * Obtiene información de configuración para debugging
   */
  public getConfigInfo() {
    const authConfig = this.getAuthConfig();

    return {
      multievent: authConfig.multievent,
      client: authConfig.client,
      defaultEventID: authConfig.defaultEventID,
      endpoint: this.getLoginEndpoint(),
      timestamp: new Date().toISOString()
    };
  }
  
  /**
   * Log de configuración para debugging
   */
  public logConfig() {
    console.log('📋 ConfigService - Configuración actual:', this.getConfigInfo());
  }

  // ==========================================
  // MÉTODOS PARA MANEJO DEL TUTORIAL
  // ==========================================

  /**
   * Verifica si el usuario ya ha visto el tutorial
   */
  public async hasSeenTutorial(): Promise<boolean> {
    try {
      const { value } = await Storage.get({ key: 'hasSeenTutorial' });
      return value === 'true';
    } catch (error) {
      console.error('Error al verificar tutorial:', error);
      return false;
    }
  }

  /**
   * Marca el tutorial como visto
   */
  public async setTutorialSeen(seen: boolean = true): Promise<void> {
    try {
      await Storage.set({ key: 'hasSeenTutorial', value: JSON.stringify(seen) });
      console.log('📚 Tutorial marcado como visto:', seen);
    } catch (error) {
      console.error('Error al marcar tutorial:', error);
    }
  }

  /**
   * Resetea el estado del tutorial (útil para testing)
   */
  public async resetTutorial(): Promise<void> {
    try {
      await Storage.remove({ key: 'hasSeenTutorial' });
      console.log('📚 Estado del tutorial reseteado');
    } catch (error) {
      console.error('Error al resetear tutorial:', error);
    }
  }

  /**
   * Obtiene información completa del estado del tutorial
   */
  public async getTutorialInfo(): Promise<{
    hasSeenTutorial: boolean;
    timestamp: string;
  }> {
    const hasSeenTutorial = await this.hasSeenTutorial();

    return {
      hasSeenTutorial,
      timestamp: new Date().toISOString()
    };
  }
}

// Exportar instancia singleton
export const configService = ConfigService.getInstance();

// Exportar funciones de conveniencia
export const buildMultiEventLoginRequest = (ced: string) => 
  configService.buildMultiEventLoginRequest(ced);

export const buildSingleEventLoginRequest = (ced: string, eventID: string) => 
  configService.buildSingleEventLoginRequest(ced, eventID);

export const getLoginEndpoint = () => 
  configService.getLoginEndpoint();

export const getLoginRequestOptions = (data: LoginRequestData) =>
  configService.getLoginRequestOptions(data);
